'use client'

import { LineChevronDownSmall } from '@qr-front/icons/line-chevron-down-small'
import { LineChevronUpSmall } from '@qr-front/icons/line-chevron-up-small'
import { LineCloseCircle } from '@qr-front/icons/line-close-circle'
import * as Popover from '@radix-ui/react-popover'
import { forwardRef, useRef, useState } from 'react'
import { cn } from 'utils/cn'

import { Checkbox } from '../../components/Checkbox'
import { SmartScrollContainer } from '../../components/SmartScrollContainer'

type TagTriggerProps = {
	selected?: string[]
	options: TDropdownOptionProps[]
	isOpen: boolean
	placeholder: string
	searchPlaceholder: string
	searchInput: string
	onSearchInputChange: (value: string) => void
	onClear: (e: React.MouseEvent<SVGSVGElement>) => void
	onFocusOpen: () => void
	disabled?: boolean
}

export type TDropdownOptionProps = {
	value: string
	label: string
}

interface TBaseSelectDropdownProps {
	options: TDropdownOptionProps[]
	selected?: string[]
	onChange?: (v: string[]) => void
	className?: string
	placeholder: string
	triggerClassName?: string
	noOptionsText?: string
	disabled?: boolean
}

interface TaggedSelectDropdownProps extends TBaseSelectDropdownProps {
	withTag: true
	searchPlaceholder: string
}

interface TUntaggedSelectDropdownProps extends TBaseSelectDropdownProps {
	withTag?: false | undefined
	searchPlaceholder?: never
}

interface TConditionalTaggedProps extends TBaseSelectDropdownProps {
	withTag?: boolean
	searchPlaceholder?: string
}

type TSelectDropdown =
	| TaggedSelectDropdownProps
	| TUntaggedSelectDropdownProps
	| TConditionalTaggedProps

const TagItem = ({
	label,
	count,
	onClear,
	isOpen,
}: {
	label: string
	count: number
	onClear: (e: React.MouseEvent<SVGSVGElement>) => void
	isOpen: boolean
}) => {
	const additionalCount = count > 1 ? ` / +${count - 1}` : ''
	return (
		<div
			className={cn(
				'flex items-center gap-2 rounded-full px-1',
				isOpen && 'gap-1 bg-light-blue',
			)}
		>
			<LineCloseCircle
				className="h-4 w-4 cursor-pointer text-grey hover:text-dark-grey"
				onClick={onClear}
			/>
			<span
				className={cn(
					'type-body-caption whitespace-nowrap text-blue',
					!isOpen && 'type-body-small font-semibold',
				)}
			>
				{label}
				{additionalCount}
			</span>
		</div>
	)
}

export const TagTrigger = forwardRef(
	(
		{
			selected,
			options,
			isOpen,
			placeholder,
			searchPlaceholder,
			searchInput,
			onSearchInputChange,
			onClear,
			onFocusOpen,
			disabled,
		}: TagTriggerProps,
		ref,
	) => {
		const inputPlaceholder = isOpen ? searchPlaceholder : placeholder
		const searchInputPlaceholder =
			selected && selected.length > 0 ? '' : inputPlaceholder

		return (
			<div className="flex min-w-0 flex-1 items-center gap-2">
				{selected?.slice(0, 1).map((value) => {
					const option = options.find((opt) => opt.value === value)
					if (!option) return null
					return (
						<TagItem
							key={value}
							label={option.label}
							count={selected.length}
							isOpen={isOpen}
							onClear={onClear}
						/>
					)
				})}
				<input
					ref={ref as React.RefObject<HTMLInputElement>}
					type="text"
					placeholder={searchInputPlaceholder}
					onFocus={disabled ? undefined : onFocusOpen}
					onClick={(e) => {
						e.stopPropagation()
						if (disabled) e.preventDefault()
					}}
					className={cn(
						'flex-1 border-none bg-transparent outline-none placeholder:text-grey',
						searchInputPlaceholder.length ? 'min-w-44' : 'min-w-0',
						!isOpen && 'w-0',
						disabled && 'cursor-not-allowed',
					)}
					value={searchInput}
					onChange={(e) => {
						if (!disabled) {
							onSearchInputChange(e.target.value)
						}
					}}
					autoComplete="off"
					tabIndex={disabled ? -1 : 0}
					disabled={disabled}
				/>
			</div>
		)
	},
)

TagTrigger.displayName = 'TagTrigger'

const SelectDropdownItem = ({
	option,
	selected,
	onOptionSelect,
	options,
}: {
	option: TDropdownOptionProps
	selected: boolean
	onOptionSelect: (option: string) => void
	options: TDropdownOptionProps[]
}) => {
	return (
		<li
			key={option.value}
			onClick={() => onOptionSelect(option.value)}
			className={cn(
				'flex min-h-10 cursor-pointer items-center gap-2 rounded-8 p-2 hover:bg-light-blue',
				selected && 'bg-light-blue',
				options.length > 6 && 'mr-2', // Add margin only when a scrollbar is present
			)}
		>
			<Checkbox id={option.value} checked={selected} />
			<span className="type-body-small min-w-0 break-words">
				{option.label}
			</span>
		</li>
	)
}

export const SelectDropdown = ({
	options,
	disabled,
	selected,
	onChange,
	placeholder,
	searchPlaceholder,
	className,
	triggerClassName,
	withTag = false,
	noOptionsText = 'No options available',
}: TSelectDropdown) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchInput, setSearchInput] = useState('')
	const inputRef = useRef<HTMLInputElement>(null)

	const activePlaceholder = isOpen ? searchPlaceholder : placeholder
	const placeholderMinWidth = activePlaceholder
		? `${activePlaceholder.length + 6}ch`
		: undefined

	const handleOptionSelect = (option: string) => {
		const updated = Array.isArray(selected)
			? selected.includes(option)
				? selected.filter((item) => item !== option)
				: [...selected, option]
			: [option]

		if (withTag) {
			setSearchInput('')
		}
		onChange?.(updated)
	}

	const handleResetSelection = (e: React.MouseEvent<SVGSVGElement>) => {
		e.stopPropagation()
		onChange?.([])
	}

	const getSelectedLabels = () => {
		return selected
			?.map(
				(value) =>
					options.find((option) => option.value === value)?.label,
			)
			.filter(Boolean)
	}

	const renderTriggerLabel = () => {
		if (withTag) {
			return null
		}

		const selectedLabels = getSelectedLabels()
		const count = selectedLabels?.length ?? 0

		const baseLabel = <span className="text-grey">{placeholder}</span>

		if (count === 0) {
			return baseLabel
		}

		const visibleLabels = selectedLabels?.slice(0, 2).join(' / ')
		const additionalCount = count > 2 ? ` / +${count - 2}` : ''

		return (
			<div className="flex items-center">
				{baseLabel}
				{`${visibleLabels ? ':' : ''}`}
				<span className="ml-1 cursor-pointer font-semibold text-blue">
					{visibleLabels}
					{additionalCount}
				</span>
			</div>
		)
	}

	const onOpenChange = (open: boolean) => {
		if (disabled && open) {
			return
		}
		setIsOpen(open)
		if (open && withTag && inputRef.current) {
			requestAnimationFrame(() => {
				inputRef.current?.focus()
			})
		}
	}

	return (
		<Popover.Root open={isOpen} onOpenChange={onOpenChange}>
			<div
				className={cn(
					'relative inline-block w-full text-left',
					className,
				)}
			>
				<Popover.Trigger asChild disabled={disabled}>
					{withTag ? (
						<div
							className={cn(
								'type-body-small flex min-h-10 w-full items-center gap-2 overflow-hidden rounded-8 border-none bg-white px-4 text-grey ring-1 ring-inset ring-border-grey transition placeholder:text-grey focus-within:outline-none focus-within:ring-2',
								triggerClassName,
								(isOpen ||
									selected?.length ||
									searchInput?.length) &&
									'desktop:max-w-[min-content]',
								disabled &&
									'pointer-events-none cursor-not-allowed opacity-50',
							)}
							style={
								placeholderMinWidth
									? { minWidth: placeholderMinWidth }
									: undefined
							}
						>
							<TagTrigger
								selected={selected}
								options={options}
								isOpen={isOpen}
								placeholder={placeholder}
								searchPlaceholder={searchPlaceholder as string}
								searchInput={searchInput}
								onSearchInputChange={setSearchInput}
								onClear={handleResetSelection}
								onFocusOpen={() => setIsOpen(true)}
								ref={inputRef}
								disabled={disabled}
							/>
							{isOpen ? (
								<LineChevronUpSmall className="h-4 w-4" />
							) : (
								<LineChevronDownSmall className="h-4 w-4" />
							)}
						</div>
					) : (
						<button
							onClick={() => setIsOpen(!isOpen)}
							disabled={disabled}
							className={cn(
								'type-body-small flex h-10 w-full items-center justify-between gap-2 overflow-hidden text-ellipsis whitespace-nowrap rounded-8 border-none bg-white px-4 text-grey ring-1 ring-inset ring-border-grey transition placeholder:text-grey focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:text-grey [&:not([disabled])]:hover:ring-2',
								triggerClassName,
							)}
						>
							<div className="flex flex-1 items-center gap-2">
								{(selected as string[])?.length > 0 && (
									<LineCloseCircle
										className="h-4 w-4 cursor-pointer"
										onClick={handleResetSelection}
									/>
								)}
								{renderTriggerLabel()}
							</div>
							{isOpen ? (
								<LineChevronUpSmall className="h-4 w-4" />
							) : (
								<LineChevronDownSmall className="h-4 w-4" />
							)}
						</button>
					)}
				</Popover.Trigger>

				<Popover.Portal>
					<Popover.Content
						align="start"
						side="bottom"
						asChild
						onOpenAutoFocus={(e) => e.preventDefault()}
						className="mt-2 max-h-[280px]"
					>
						<div
							className="absolute left-0 z-50 flex w-40 min-w-[var(--radix-popover-trigger-width)] flex-col rounded-8 bg-white p-2 shadow-icon outline-none focus:outline-none focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2"
							tabIndex={-1}
						>
							<SmartScrollContainer
								direction="vertical"
								scrollbarPaddingHeight={0}
								maxHeight={270}
							>
								<ul className="flex w-full flex-col gap-1">
									{(() => {
										const filteredOptions = options.filter(
											(option) =>
												withTag
													? option?.label
															?.toLowerCase()
															?.includes(
																searchInput.toLowerCase(),
															)
													: true,
										)

										if (filteredOptions.length === 0) {
											return (
												<li className="flex items-center justify-center p-2">
													<span
														className={`type-body-small ${noOptionsText?.length < 22 ? 'whitespace-nowrap' : ''} text-grey`}
													>
														{noOptionsText}
													</span>
												</li>
											)
										}

										return filteredOptions.map((option) => {
											const isSelected =
												selected?.includes(
													option.value,
												) ?? false
											return (
												<SelectDropdownItem
													key={option.value}
													option={option}
													selected={isSelected}
													onOptionSelect={
														handleOptionSelect
													}
													options={options}
												/>
											)
										})
									})()}
								</ul>
							</SmartScrollContainer>
						</div>
					</Popover.Content>
				</Popover.Portal>
			</div>
		</Popover.Root>
	)
}
